
import React from 'react';
import { SyncSources } from '../types';

interface Props {
  isConnected: boolean;
  isSyncing: boolean;
  syncDays: number;
  setSyncDays: (days: number) => void;
  onConnect: () => void;
  onSync: () => void;
  selectedSources: SyncSources;
  onSourcesChange: (sources: SyncSources) => void;
}

const GoogleSync: React.FC<Props> = ({ 
  isConnected, 
  isSyncing, 
  syncDays, 
  setSyncDays, 
  onConnect, 
  onSync,
  selectedSources,
  onSourcesChange
}) => {
  const toggleSource = (source: keyof SyncSources) => {
    onSourcesChange({
      ...selectedSources,
      [source]: !selectedSources[source]
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden relative">
      {isConnected && (
        <div className="absolute top-5 right-5">
          <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
        </div>
      )}

      <div className="p-5">
        {!isConnected ? (
          <button 
            onClick={onConnect}
            className="w-full flex items-center justify-center space-x-2 bg-slate-900 text-white py-3 rounded-xl hover:bg-slate-950 transition-colors text-sm font-bold shadow-lg shadow-slate-200"
          >
            Authorize Google Account
          </button>
        ) : (
          <div className="space-y-6">
            <div className="space-y-3">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider px-1">Data Sources</label>
              <div className="grid grid-cols-4 gap-1.5">
                <button 
                  onClick={() => toggleSource('gmail')}
                  className={`flex items-center justify-center space-x-1.5 p-2 rounded-xl border transition-all text-[10px] font-bold ${
                    selectedSources.gmail 
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                    : 'bg-white border-slate-200 text-slate-400 opacity-60'
                  }`}
                >
                  <span className="text-sm">📧</span>
                  <span>Gmail</span>
                </button>
                <button 
                  onClick={() => toggleSource('drive')}
                  className={`flex items-center justify-center space-x-1.5 p-2 rounded-xl border transition-all text-[10px] font-bold ${
                    selectedSources.drive 
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                    : 'bg-white border-slate-200 text-slate-400 opacity-60'
                  }`}
                >
                  <span className="text-sm">📁</span>
                  <span>Drive</span>
                </button>
                <button 
                  onClick={() => toggleSource('calendar')}
                  className={`flex items-center justify-center space-x-1.5 p-2 rounded-xl border transition-all text-[10px] font-bold ${
                    selectedSources.calendar 
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                    : 'bg-white border-slate-200 text-slate-400 opacity-60'
                  }`}
                >
                  <span className="text-sm">📅</span>
                  <span>Calendar</span>
                </button>
                <button 
                  onClick={() => toggleSource('chat')}
                  className={`flex items-center justify-center space-x-1.5 p-2 rounded-xl border transition-all text-[10px] font-bold ${
                    selectedSources.chat 
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                    : 'bg-white border-slate-200 text-slate-400 opacity-60'
                  }`}
                >
                  <span className="text-sm">💬</span>
                  <span>Chat</span>
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center px-1">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Lookback Period</label>
                <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">Last {syncDays} days</span>
              </div>
              <input 
                type="range" 
                min="1" 
                max="30" 
                value={syncDays} 
                onChange={(e) => setSyncDays(parseInt(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
              <div className="flex justify-between text-[9px] text-slate-400 font-medium px-1">
                <span>1 day</span>
                <span>15 days</span>
                <span>30 days</span>
              </div>
            </div>

            <button 
              onClick={onSync}
              disabled={isSyncing}
              className={`w-full flex items-center justify-center space-x-2 py-3.5 rounded-xl transition-all text-sm font-bold shadow-lg transform active:scale-95 ${
                isSyncing 
                  ? 'bg-slate-100 text-slate-400 shadow-none' 
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-100'
              }`}
            >
              <svg className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{isSyncing ? 'Pulling Context...' : 'Sync Workspace'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleSync;
